// FILE: Calls.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from './config';
import './Calls.css'; // Importa o CSS global para Calls.js

function Calls({ token, userRole, username }) { 
  const [calls, setCalls] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(null);
  
  const [serverId, setServerId] = useState(''); 
  const [availableServers, setAvailableServers] = useState([]); 
  const [fetchingServers, setFetchingServers] = useState(false); 
  const [serverError, setServerError] = useState(''); 
  
  const [isAdmin, setIsAdmin] = useState(userRole === 'admin');
  const [userRamais, setUserRamais] = useState([]); 

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 50,
    hasPreviousPage: false,
    hasNextPage: false
  });

  const getToday = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getOldestAllowedDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 15);
    return date.toISOString().split('T')[0];
  };

  const formatDateBR = (dateString) => {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const [filters, setFilters] = useState({
    ramal: '',
    dataInicio: getToday(),
    dataFim: getToday()
  });
  
  const isShowingToday = () => {
    const today = getToday();
    return filters.dataInicio === today && filters.dataFim === today && filters.ramal === '';
  };

  const oldestAllowedDate = getOldestAllowedDate();

  useEffect(() => {
    setIsAdmin(userRole === 'admin');

    const determineServerIdAndFetchAvailableServers = async () => {
      if (username === 'spseguranca') {
        console.log("Usuário especial detectado - forçando servidor 3");
        setServerId('3');
        return;
      }
      
      if (userRole === 'user') {
        try {
          const defaultServerResponse = await axios.get(`${API_URL}/users/servers/default`, {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          });
          
          if (defaultServerResponse.data && defaultServerResponse.data.id) {
            console.log(`Servidor padrão definido automaticamente: ${defaultServerResponse.data.id}`);
            setServerId(defaultServerResponse.data.id.toString());
            return;
          }
        } catch (serverErr) {
          console.error('Erro ao buscar servidor padrão:', serverErr);
          setServerId('1'); 
        }
      } 
      else if (userRole === 'admin') { 
        setFetchingServers(true);
        setServerError('');
        try {
          const response = await axios.get(`${API_URL}/users/servers/active`, {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          });
          
          console.log("Servidores recebidos:", response.data);
          
          if (Array.isArray(response.data) && response.data.length > 0) {
            setAvailableServers(response.data);
            setServerId(response.data[0].id.toString());
          } else {
            setAvailableServers([]);
            setServerId('1'); 
          }
        } catch (err) {
          console.error('Erro ao buscar servidores:', err);
          setServerError('Erro ao carregar servidores. Usando servidor padrão.');
          setAvailableServers([]);
          setServerId('1');
        } finally {
          setFetchingServers(false);
        }
      } else {
        setServerId('1');
      }
    };

    determineServerIdAndFetchAvailableServers();
  }, [token, userRole, username]); 

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    setError('');
    
    const effectiveServerId = serverId || '1';
    
    try {
      console.log(`Buscando chamadas com serverId: ${effectiveServerId}`);
      
      const queryParams = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.recordsPerPage,
        ...filters,
        serverId: effectiveServerId
      });

      const response = await axios.get(`${API_URL}/calls?${queryParams}`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      const callsWithServerId = response.data.calls.map(call => ({
        ...call,
        serverId: effectiveServerId 
      }));
      
      setCalls(callsWithServerId);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));

    } catch (err) {
      console.error("Erro ao buscar chamadas:", err);
      let errorMessage = 'Erro ao buscar chamadas. ';
      
      if (err.response) {
        errorMessage += `Código: ${err.response.status}. `;
        if (err.response.data && err.response.data.message) {
          errorMessage += `Detalhe: ${err.response.data.message}`;
        }
      } else if (err.request) {
        errorMessage += 'Servidor não respondeu. Verifique sua conexão.';
      } else {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.recordsPerPage, filters, serverId, token]);

  useEffect(() => {
    if (serverId) {
      fetchCalls();
    }
  }, [fetchCalls, serverId]); 

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchCalls();
  };

  const handleClearFilters = () => {
    setFilters({
      ramal: '',
      dataInicio: getToday(),
      dataFim: getToday()
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchCalls();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ 
      ...prev, 
      recordsPerPage: newLimit, 
      currentPage: 1 
    }));
    fetchCalls();
  };

  const renderPaginationControls = (position) => (
    <div className={position === 'top' ? 'paginationControlsTop' : 'paginationControlsBottom'}>
      <button
        onClick={() => handlePageChange(pagination.currentPage - 1)}
        disabled={!pagination.hasPreviousPage}
        className="paginationButton"
      >
        Anterior
      </button>

      <span>
        Página {pagination.currentPage} de {pagination.totalPages}
      </span>

      <button
        onClick={() => handlePageChange(pagination.currentPage + 1)}
        disabled={!pagination.hasNextPage}
        className="paginationButton"
      >
        Próxima
      </button>
    </div>
  );

  const handleDownload = async (call) => {
    if (!call || !call.uniqueid || !call.calldate) { 
      setError('Dados de gravação inválidos (ID ou data da chamada ausente).');
      return;
    }
    
    setError('');
    setDownloadingFile(call.uniqueid);
    
    try {
      const effectiveServerId = username === 'spseguranca' ? '3' : (call.serverId || serverId);
      
      const datePart = call.calldate.split(' ')[0]; 
      let year, month, day;

      const parts = datePart.split('/'); 

      if (parts.length === 3) {
        day = parts[0];
        month = parts[1];
        let twoDigitYear = parts[2];

        if (twoDigitYear.length === 2) { 
          const currentYearFull = new Date().getFullYear(); 
          const currentYearLastTwoDigits = currentYearFull % 100; 
          const currentCentury = Math.floor(currentYearFull / 100) * 100; 

          const parsedTwoDigitYear = parseInt(twoDigitYear, 10);

          if (parsedTwoDigitYear <= currentYearLastTwoDigits + 10) { 
            year = (currentCentury + parsedTwoDigitYear).toString();
          } else {
            year = (currentCentury - 100 + parsedTwoDigitYear).toString(); 
          }
        } else { 
          year = twoDigitYear;
        }
      } else {
        console.error("Formato de data inesperado em call.calldate, esperando DD/MM/YY(YY):", call.calldate);
        setError("Formato de data da chamada inesperado. Não foi possível baixar a gravação.");
        setDownloadingFile(null);
        return;
      }

      year = String(year).padStart(4, '0'); 
      month = String(month).padStart(2, '0');
      day = String(day).padStart(2, '0');

      const formattedRecordingPath = `${year}/${month}/${day}`;
      
      console.log("Original call.calldate:", call.calldate);
      console.log("Data extraída (DD, MM, YYYY):", day, month, year);
      console.log("recordingPath formatado para o backend (YYYY/MM/DD):", formattedRecordingPath);
      
      const url = `${API_URL}/download/${call.uniqueid}?serverId=${effectiveServerId}&recordingPath=${encodeURIComponent(formattedRecordingPath)}`;
      console.log(`Tentando download com URL: ${url}`);
      
      const response = await axios({
        method: 'get',
        url: url,
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob',
        timeout: 15000
      });
      
      if (response.data.size === 0) {
        throw new Error(`Resposta vazia para a gravação ${call.uniqueid} na data ${datePart}. O arquivo pode não existir para esta data.`);
      }
      
      console.log(`Sucesso com a data ${datePart}!`, response.status);
      
      const contentType = response.headers['content-type'];
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: contentType || 'audio/wav' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      
      let filename = `gravacao-${call.uniqueid}-srv${effectiveServerId}-${year}-${month}-${day}.wav`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      console.log(`Download finalizado: ${filename}`);
      
    } catch (err) {
      console.error('Erro ao baixar gravação:', err);
      let errorMessage = 'Erro ao baixar gravação';
      
      if (err.response) {
        errorMessage += `: ${err.response.status} - ${err.response.statusText || 'Erro no servidor'}`;
        if (err.response.data && err.response.data.message) {
            errorMessage += ` Detalhe: ${err.response.data.message}`;
        }
      } else if (err.request) {
        errorMessage += ': Servidor não respondeu. Verifique sua conexão.';
      } else {
        errorMessage += `: ${err.message || 'Erro desconhecido'}`;
      }
      
      setError(errorMessage);
    } finally {
      setTimeout(() => {
        setDownloadingFile(null);
      }, 3000);
    }
  };

  return (
    <div className="callsContainer">
      {/* Aviso sobre limite de 15 dias */}
      <div className="alertMessage alertWarning">
        ⚠️ Atenção: Somente gravações dos últimos 15 dias estão disponíveis.
      </div>

      {/* Banner verde só aparece quando mostra apenas hoje */}
      {isShowingToday() && (
        <div className="alertMessage alertSuccess">
          ✅ Exibindo chamadas de hoje ({formatDateBR(getToday())}). Use os filtros abaixo para alterar o período.
        </div>
      )}

      {/* Seção de filtros */}
      <div className="filterSection">
        <h4>Filtros de Pesquisa</h4>
        <div className="filterGrid">
          {/* Campo de seleção para PBX IP (apenas para admin) */}
          {isAdmin && (
            <div className="formGroup">
              <label className="formLabel">PBX IP:</label>
              <select 
                value={serverId} 
                onChange={(e) => setServerId(e.target.value)} 
                className="formSelect"
              >
                {availableServers.map(server => (
                  <option key={server.id} value={server.id.toString()}>
                    {server.name || `PBX ${server.id}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="formGroup">
            <label className="formLabel">Ramal:</label>
            <input
              type="text"
              value={filters.ramal}
              onChange={(e) => setFilters({...filters, ramal: e.target.value})}
              placeholder="Digite o ramal"
              className="formInput"
            />
          </div>
          <div className="formGroup">
            <label className="formLabel">Data Início:</label>
            <input
              type="date"
              value={filters.dataInicio}
              min={oldestAllowedDate}
              max={getToday()}
              onChange={(e) => setFilters({...filters, dataInicio: e.target.value})}
              className="formInput"
            />
            <small className="dateInfoSmall">
              Data mais antiga disponível: {formatDateBR(oldestAllowedDate)}
            </small>
          </div>
          <div className="formGroup">
            <label className="formLabel">Data Fim:</label>
            <input
              type="date"
              value={filters.dataFim}
              min={oldestAllowedDate}
              max={getToday()}
              onChange={(e) => setFilters({...filters, dataFim: e.target.value})}
              className="formInput"
            />
          </div>
        </div>
        <div className="filterButtons">
          <button 
            onClick={handleSearch}
            className="button buttonPrimary"
          >
            Pesquisar
          </button>
          <button 
            onClick={handleClearFilters}
            className="button buttonSecondary"
          >
            Voltar para Hoje
          </button>
        </div>
      </div>

      {/* Exibir erro se houver */}
      {error && (
        <div className="errorMessage">
          <div>
            <strong>Erro: </strong>{error}
          </div>
          <button 
            onClick={() => setError('')} 
            className="closeErrorButton"
          >
            ×
          </button>
        </div>
      )}
        <div>
          Total: {pagination.totalRecords} registros
        </div>

      {/* Controles de paginação superiores */}
      {!loading && calls.length > 0 && renderPaginationControls('top')}

      {/* Loading */}
      {loading && (
        <div className="loadingMessage">
          Carregando gravações...
        </div>
      )}

      {/* Tabela de chamadas */}
      {!loading && calls.length > 0 && (
        <table className="callsTable">
          <thead>
            <tr>
              <th className="tableHeaderCell">Data/Hora</th>
              <th className="tableHeaderCell">Origem</th>
              <th className="tableHeaderCell">Destino</th>
              <th className="tableHeaderCell">Duração (seg)</th>
              <th className="tableHeaderCell">Status</th>
              <th className="tableHeaderCell">Gravação</th>
            </tr>
          </thead>
          <tbody>
            {calls.map((call, idx) => (
              <tr key={`${call.uniqueid}-${idx}`}>
                <td className="tableCell">
                  {call.calldate}
                </td>
                <td className="tableCell">
                  {call.src}
                </td>
                <td className="tableCell">
                  {call.dst}
                </td>
                <td className="tableCell">
                  {call.duration}
                </td>
                <td className="tableCell">
                  {call.disposition}
                </td>
                <td className="tableCell centered">
                  {call.recordingfile ? (
                    <button 
                      onClick={() => handleDownload(call)}
                      disabled={downloadingFile === call.uniqueid}
                      className="downloadButton"
                    >
                      {downloadingFile === call.uniqueid ? 'Baixando...' : 'Download'}
                    </button>
                  ) : (
                    <span className="noRecordingText">
                      Sem gravação
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Controles de paginação inferiores */}
      {!loading && calls.length > 0 && renderPaginationControls('bottom')}

      {calls.length === 0 && !loading && (
        <div className="noCallsMessage">
          Nenhuma chamada encontrada para os filtros selecionados
        </div>
      )}
    </div>
  );
}

export default Calls;
